import { useContext, useMemo, useState } from 'react'
import NotificationForm from '../NotificationForm'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { cloneDeep, groupBy } from 'lodash'
import { DataTable, EmptyMessageWithLoader } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { CirclePause, TriangleAlert } from 'lucide-react'
import {
  useDiscordChannelDetails,
  useNotifications,
  useNotificationTemplates,
} from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import TemplateContextMenu from '../TemplateContextMenu'
import { buildNotificationTemplateColumns } from '../notificationTemplateColumns'
import dayjs from 'dayjs'
import Timestamp from 'app/frontend/base/components/Timestamp'
import NotificationWarningModal from '../NotificationWarningModal'
import { Link } from 'react-router-dom'
import { FormStateContext } from 'app/frontend/base/config/context/forms'

const newNotification: Partial<wowaudit.NotificationTemplate> = {}

const mostRecentTimestamp = (timestamps: (string | undefined)[]) => {
  return timestamps.reduce<string | undefined>((latest, timestamp) => {
    if (!timestamp) return latest
    if (!latest || dayjs(timestamp).isAfter(dayjs(latest))) return timestamp

    return latest
  }, undefined)
}

const NotificationSection = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { handleSectionChange } = useContext(FormStateContext)
  const [openTemplate, setOpenTemplate] = useState<
    Partial<wowaudit.NotificationTemplate> | undefined
  >()
  const {
    data: templates,
    error,
    isPending,
    isLoadingError,
  } = useNotificationTemplates(team.id)
  const { data: notifications } = useNotifications(team.id)
  const discordAddresses = useMemo(
    () =>
      (templates ?? []).flatMap((template) =>
        template.targetAddress ? [template.targetAddress] : [],
      ),
    [templates],
  )
  const { data: discordChannelDetails, isPending: discordDetailsPending } =
    useDiscordChannelDetails(discordAddresses)
  const tablePending =
    isPending || (discordAddresses.length > 0 && discordDetailsPending)
  const [openWarningTemplateId, setOpenWarningTemplateId] = useState<
    number | null
  >(null)

  const mostRecentNotificationsByTemplate = useMemo(() => {
    return notifications?.reduce((acc, notification) => {
      if (!acc[notification.notificationTemplateId]) {
        acc[notification.notificationTemplateId] = notification
      } else {
        const existing = acc[notification.notificationTemplateId]
        if (
          dayjs(notification.lastAttemptedAt).isAfter(
            dayjs(existing.lastAttemptedAt),
          )
        ) {
          acc[notification.notificationTemplateId] = notification
        }
      }
      return acc
    }, {} as Record<number, wowaudit.Notification>)
  }, [notifications])

  const failedNotificationsByTemplate = useMemo(() => {
    return groupBy(
      (notifications ?? []).filter(
        (notification) => notification.deliveryStatus === 'failed',
      ),
      'notificationTemplateId',
    )
  }, [notifications])

  const columns: ColumnDef<wowaudit.NotificationTemplate>[] = useMemo(
    () => [
      ...buildNotificationTemplateColumns({
        team,
        discordChannelDetails: discordChannelDetails ?? [],
        t,
      }),
      {
        header: 'Last triggered',
        id: 'lastAttemptedAt',
        cell: (cell) => {
          const template = cell.row.original
          const notification = mostRecentNotificationsByTemplate?.[template.id]
          const lastTriggeredAt = template.cronSchedule
            ? mostRecentTimestamp([
                template.lastScheduledAt,
                notification?.lastAttemptedAt,
              ])
            : notification?.lastAttemptedAt

          return (
            <Timestamp
              className='font-normal'
              time={lastTriggeredAt}
              markOutdatedDays={7}
            />
          )
        },
      },
      {
        header: 'Warnings',
        id: 'warnings',
        size: 148,
        cell: (cell) => {
          const templateId = cell.row.original.id
          const failedNotifications = failedNotificationsByTemplate[templateId]
          const warning = cell.row.original.suspended ? (
            <span className='flex items-center gap-2 whitespace-nowrap text-info-foreground'>
              <CirclePause size={16} /> {t('notifications.suspended')}
            </span>
          ) : !cell.row.original.active ? (
            <Tooltip>
              <TooltipTrigger>
                <span className='flex items-center gap-2 whitespace-nowrap text-warning-foreground'>
                  <TriangleAlert size={16} />{' '}
                  {t('shared.inactive').toLowerCase()}
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                <p>
                  This premium template is inactive because the team does not
                  have enough boosts applied. Visit the{' '}
                  <Link
                    to={`${team.path}/settings#premium`}
                    onClick={() => handleSectionChange('premium')}
                    className='text-primary'
                  >
                    premium
                  </Link>{' '}
                  tab to apply more boosts.
                </p>
              </TooltipContent>
            </Tooltip>
          ) : failedNotifications ? (
            <span
              className='flex items-center gap-2 whitespace-nowrap text-warning-foreground cursor-pointer'
              onClick={() => setOpenWarningTemplateId(templateId)}
              role='button'
              tabIndex={0}
            >
              <TriangleAlert size={16} /> {failedNotifications?.length} failed
            </span>
          ) : null

          return warning
        },
      },
      {
        header: '',
        id: 'actions',
        size: 50,
        cell: ({ row }) => {
          const failedNotifications =
            failedNotificationsByTemplate[row.original.id]

          return (
            <TemplateContextMenu
              template={row.original}
              setOpenTemplate={setOpenTemplate}
              setOpenWarningModal={
                failedNotifications?.length
                  ? setOpenWarningTemplateId
                  : undefined
              }
            />
          )
        },
      },
    ],
    [
      discordChannelDetails,
      mostRecentNotificationsByTemplate,
      failedNotificationsByTemplate,
    ],
  )

  if (openTemplate) {
    return (
      <>
        <div className='flex items-center justify-between'>
          <h4>
            {t(
              `notifications.edit-modal-title-${
                openTemplate?.id ? 'existing' : 'new'
              }`,
            )}
          </h4>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => setOpenTemplate(undefined)}
          >
            {t('shared.back-to-list')}
          </Button>
        </div>

        <p className='text-sm mb-8'>
          {t(
            `notifications.edit-modal-intro-${
              openTemplate?.id ? 'existing' : 'new'
            }`,
          )}
        </p>

        <NotificationForm
          key={openTemplate?.id ?? 'new'}
          setTemplate={setOpenTemplate}
          template={openTemplate ?? {}}
        />
      </>
    )
  } else {
    return (
      <div>
        <div className='flex justify-between'>
          <h4>{t('notifications.title')}</h4>
          <Button
            size='sm'
            onClick={() => setOpenTemplate(cloneDeep(newNotification))}
          >
            {t('shared.create-new')}
          </Button>
        </div>

        <p className='mt-4 mb-6'>{t('notifications.introduction')}</p>

        <DataTable
          columns={columns}
          data={tablePending ? [] : templates ?? []}
          includeHeader={true}
          emptyMessage={() => (
            <EmptyMessageWithLoader
              emptyMessageKey='notifications.empty-message'
              isPending={tablePending}
              isLoadingError={isLoadingError}
              error={error}
              t={t}
            />
          )}
        />
        {openWarningTemplateId !== null && (
          <NotificationWarningModal
            open={openWarningTemplateId !== null}
            onOpenChange={(open) => {
              if (!open) setOpenWarningTemplateId(null)
            }}
            notifications={
              failedNotificationsByTemplate[openWarningTemplateId] || []
            }
          />
        )}
      </div>
    )
  }
}

export default NotificationSection
